<svelte:options runes={false} />

<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, InputV2, Text, TokenV2 } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { compareFWVersions } from 'src/helpers/firmware_compare_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { capFirstChar, getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import SettingGuide from '../SettingGuide.svelte';

	export let device: Device;

	const offsetMin = -50;
	const offsetMax = 50;
	const thresholdMin = 0.5;
	const thresholdMax = 5;
	const settings = readDeviceSettings(real_id(device.id));
	const temperatureSettings = settings?.['temperature:0'];
	const initialUnit: 'C' | 'F' =
		(settings?.ht_ui?.temperature_unit ?? device.ht_ui?.temperature_unit) === 'F' ? 'F' : 'C';
	const initialOffset = Number(temperatureSettings?.offset_C ?? 0);
	const initialThreshold = Number(temperatureSettings?.report_thr_C ?? thresholdMin);

	let selectedUnit: 'C' | 'F' = initialUnit;
	let offsetValue = String(initialOffset);
	let thresholdValue = String(initialThreshold);
	let savedUnit = initialUnit;
	let savedOffset = initialOffset;
	let savedThreshold = initialThreshold;
	let loading = false;

	$: deviceFwVersion =
		(
			settings?.DeviceInfo?.ver ||
			settings?.sys?.device?.fw_id ||
			settings?.DeviceInfo?.fw_id
		)?.match(/(\d{1,}\.){1,}\d{1,}/)?.[0] || '0.0.1';
	$: supportsOffset = compareFWVersions(deviceFwVersion, '>=', '0.11.4');
	$: numericOffset =
		offsetValue === '' || offsetValue === null || offsetValue === undefined
			? NaN
			: Number(offsetValue);
	$: numericThreshold =
		thresholdValue === '' || thresholdValue === null || thresholdValue === undefined
			? NaN
			: Number(thresholdValue);
	$: offsetInputData = getErrorOrInfoMessageForMinAndMax(
		numericOffset,
		offsetMin,
		offsetMax,
	);
	$: thresholdInputData = getErrorOrInfoMessageForMinAndMax(
		numericThreshold,
		thresholdMin,
		thresholdMax,
	);
	$: unitsDirty = selectedUnit !== savedUnit;
	$: offsetDirty = supportsOffset && numericOffset !== savedOffset;
	$: thresholdDirty = numericThreshold !== savedThreshold;
	$: isDirty = unitsDirty || offsetDirty || thresholdDirty;
	$: hasError =
		(supportsOffset && !!offsetInputData.error) || !!thresholdInputData.error;
	$: saveDisabled = loading || !isDirty || hasError;

	const save = async () => {
		if (saveDisabled) return;

		const unitToSave = selectedUnit;
		const offsetToSave = numericOffset;
		const thresholdToSave = numericThreshold;
		const saveUnits = unitsDirty;
		const saveOffset = offsetDirty;
		const saveThreshold = thresholdDirty;
		const responses: SettingRequest[] = [];

		loading = true;

		if (saveUnits) {
			const res: SettingRequest = await gen2Request(device, 'ht_ui.SetConfig', {
				config: { temperature_unit: unitToSave },
			});
			responses.push(res);
			if (res?.isok) savedUnit = unitToSave;
		}

		if (saveOffset || saveThreshold) {
			const config: Record<string, number> = {};
			if (saveOffset) config.offset_C = offsetToSave;
			if (saveThreshold) config.report_thr_C = thresholdToSave;

			const res: SettingRequest = await gen2Request(device, 'Temperature.SetConfig', {
				id: device.channel,
				config,
			});
			responses.push(res);
			if (res?.isok) {
				if (saveOffset) savedOffset = offsetToSave;
				if (saveThreshold) savedThreshold = thresholdToSave;
			}
		}

		if (responses.length > 0) onSettingsResponse(responses, device);
		loading = false;
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		setStickyBottomAction({
			onClick: save,
			loading,
			disabled: saveDisabled,
			icon: 'fas fa-save',
		});
	}
</script>

<div class="temperature-settings">
	<SettingGuide
		text={$format('settings_guide_temperature_sensor', {
			default:
				'Choose temperature units, calibrate readings, and decide when temperature changes are reported.',
		})}
	/>

	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{$format('sub_settings_title_temperature_unit', {
				default: 'Temperature units',
			})}
		</Text>
		<Text styleType="body-small" class="mb-s text-color-muted">
			{$format('select_temperature_units', {
				default: 'Choose temperature units:',
			})}
		</Text>
		<div class="d-flex">
			<TokenV2
				id="token-temperature-settings-unit-c"
				selected={selectedUnit === 'C'}
				size="m"
				title="°C"
				class="mr-s"
				on:click={() => {
					selectedUnit = 'C';
				}}
			/>
			<TokenV2
				id="token-temperature-settings-unit-f"
				selected={selectedUnit === 'F'}
				size="m"
				title="°F"
				on:click={() => {
					selectedUnit = 'F';
				}}
			/>
		</div>
	</Container>

	{#if supportsOffset}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{capFirstChar(
					$format('sub_settings_title_temperature_offset', {
						default: 'temperature offset',
					}),
				)}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('text_sensor_settings_temperature', {
					values: { unit: selectedUnit },
					default: 'Offset in °{unit} to be applied to the measured temperature.',
				})}
			</Text>
			<InputV2
				{...getNumberInputConfig(true)}
				id="input-temperature-settings-offset"
				aria-label={$format('input_temperature_offset', {
					default: 'Enter the temperature offset. Value must be between {min} — {max}!',
					values: { min: offsetMin, max: offsetMax },
				})}
				icon={null}
				size="m"
				width="full"
				min={offsetMin}
				max={offsetMax}
				step={1}
				bind:value={offsetValue}
				info={offsetInputData.info}
				error={offsetInputData.error}
			/>
		</Container>
	{/if}

	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{$format('sub_settings_title_temperature_threshold', {
				default: 'Temperature threshold',
			})}
		</Text>
		<Text styleType="body-small" class="mb-s text-color-muted">
			{$format('alert_info_message_temp_thres_trig_sensor_data', {
				values: { unit: selectedUnit },
				default:
					'Temperature change with more than the specified value in °{unit} triggers sensor data report.',
			})}
		</Text>
		<InputV2
			{...getNumberInputConfig(true)}
			id="input-temperature-settings-threshold"
			aria-label={$format('sub_settings_title_temperature_threshold', {
				default: 'Temperature threshold',
			})}
			icon={null}
			size="m"
			width="full"
			min={thresholdMin}
			max={thresholdMax}
			step={0.5}
			bind:value={thresholdValue}
			info={thresholdInputData.info}
			error={thresholdInputData.error}
		/>
	</Container>
</div>

<style>
	.temperature-settings {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
